import React, { useEffect, useState } from 'react';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '~/components/ui/dialog';
import Pencil from 'lucide-react/dist/esm/icons/pencil';
import Trash2 from 'lucide-react/dist/esm/icons/trash-2';
import ContactPicker from '~/modules/components/global/contact-picker';
import _ from 'lodash';
import confirm from '~/modules/components/global/confirm';
import PropTypes from 'prop-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ContactListsService } from '~/modules/services/contact-lists-service';
import Spinner from '~/modules/components/global/spinner';
import { captureError } from '../../../pixwel/exception-handler.factory';
import ToastService from '~/modules/services/toast-service';

// eslint-disable-next-line max-len, no-useless-escape
const EMAIL_REGEX = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const ContactListPreferences = ({ currentPreferences, isSaving, updatePreferences, session }) => {
	const [contactListFormToggle, setContactListFormToggle] = useState(false);
	const [name, setName] = useState('');
	const [recipients, setRecipients] = useState([]);
	const [edit, setEdit] = useState(false);

	const queryClient = useQueryClient();

	useEffect(() => {
		return () => {
			queryClient.removeQueries(['getGroupsAndUsers']);
		};
	}, []);

	const onFetchData = (d, error) => {
		if(error) {
			return;
		}
		let contacts = [];
		let emails = [];
		if(edit?.recipients?.users) {
			emails = edit.recipients.users?.filter((e) => EMAIL_REGEX.test(e.toLowerCase()));
		}
		if(d.groups) {
			contacts = [...contacts, ...d.groups.nodes];
		}
		if(d.users) {
			contacts = [...contacts, ...d.users.nodes];
		}
		setRecipients([...contacts, ...emails]);
	};

	const { isFetching } = useQuery(
		['getGroupsAndUsers'],
		() => ContactListsService.getGroupsAndUsers(
			edit?.recipients?.groups?.filter(Boolean),
			edit?.recipients?.users?.filter((e) => !EMAIL_REGEX.test(e.toLowerCase()))
		),
		{
			enabled: !!edit,
			onSettled: onFetchData,
			refetchOnWindowFocus: false
		}
	);

	const isSaveDisabled = () => {
		return isSaving || (_.isEqual(name, '') || _.isEqual(recipients, []))
			|| (_.isEqual(edit.name, name)
				&& _.isEqual(edit.recipients, getIdsFromRecipients(recipients)));
	};

	const getIdsFromRecipients = (r) => {
		const result = { groups: [], users: [] };
		r.forEach((item) => {
			if(typeof item === 'string') {
				result.users.push(item);
			}
			else if(item.emailNormal) {
				result.users.push(item.id + '');
			}
			else {
				result.groups.push(item.id + '');
			}
		});
		return result;
	};

	const clearForm = () => {
		setContactListFormToggle(false);
		setEdit(false);
		setName('');
		setRecipients([]);
	};

	const updateData = async(updatedPreferences, isEdit) => {
		try {
			await updatePreferences(updatedPreferences);
			ToastService.create('success', `Custom Group ${isEdit ? 'edited' : 'saved'} successfully`);
			clearForm();
		}
		catch(err) {
			captureError(err, `Error saving contact list: ${err.message}`);
		}
	};

	const handleSave = async() => {
		const updatedPreferences = currentPreferences;
		if(!Object.prototype.hasOwnProperty.call(currentPreferences, 'contactLists')) {
			updatedPreferences.contactLists = [];
		}
		if(edit) {
			updatedPreferences.contactLists[edit.key] = { name, recipients: getIdsFromRecipients(recipients) };
		}
		else {
			updatedPreferences.contactLists = [
				...(currentPreferences?.contactLists ?? []),
				{ name, recipients: getIdsFromRecipients(recipients) }
			];
		}
		await updateData(updatedPreferences, edit);
	};

	const onEditClick = (row) => {
		setEdit(row);
		setName(row.name);
		setContactListFormToggle(true);
	};

	const onDeleteClick = async(row) => {
		const confirmed = await confirm({
			title: 'Delete Contact List',
			content: `Are you sure you want to delete this contact list ${row?.name}?`,
			confirmText: 'Delete',
			confirmVariant: 'danger',
			cancelText: 'Cancel',
			cancelVariant: 'secondary'
		});
		if(confirmed) {
			currentPreferences?.contactLists.splice(row.key, 1);
			await updateData(currentPreferences);
		}
	};

	const groups = recipients.filter((r) => !r?.emailNormal && typeof r !== 'string');
	const users = recipients.filter((r) => r?.emailNormal || typeof r === 'string');

	const contactLists = _.get(currentPreferences, 'contactLists', []).map((d, key) => ({ ...d, key }));

	return (
		<div data-testid="contact-list-container" className="space-y-4">
			<div className="flex justify-end">
				<Button data-testid="add-contact-list-btn" onClick={() => setContactListFormToggle(true)}>
					Create a new Custom Group
				</Button>
			</div>

			<div className="overflow-y-auto rounded-md border border-border" style={{ maxHeight: '22rem' }}>
				{isSaving && (
					<div className="flex items-center justify-center p-8">
						<Spinner />
					</div>
				)}

				{!isSaving && contactLists.length === 0 && (
					<div className="flex items-center justify-center p-8 text-sm text-muted-foreground">
						No Custom Groups
					</div>
				)}

				{!isSaving && contactLists.length > 0 && (
					<table className="w-full text-sm">
						<thead>
							<tr className="border-b border-border bg-muted/40">
								<th className="px-4 py-2 text-left font-medium text-muted-foreground">Name</th>
								<th className="px-4 py-2 text-right font-medium text-muted-foreground">Actions</th>
							</tr>
						</thead>
						<tbody>
							{contactLists.map((row) => (
								<tr key={row.key} className="border-b border-border last:border-0 hover:bg-muted/20 transition-colors">
									<td className="px-4 py-3 truncate max-w-0" style={{ width: '100%' }}>
										{row.name}
									</td>
									<td className="px-4 py-3 whitespace-nowrap">
										<div className="flex items-center justify-end gap-2">
											<Button
												variant="outline"
												size="sm"
												data-testid="contact-list-edit-button"
												onClick={() => onEditClick(row)}
											>
												<Pencil className="mr-1 h-3 w-3" /> Edit
											</Button>
											<Button
												variant="destructive"
												size="sm"
												data-testid="contact-list-delete-button"
												onClick={() => onDeleteClick(row)}
											>
												<Trash2 className="mr-1 h-3 w-3" /> Delete
											</Button>
										</div>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				)}
			</div>

			<Dialog
				open={contactListFormToggle}
				onOpenChange={(open) => {
					if(!open) {
						clearForm();
					}
				}}
			>
				<DialogContent
					className="rounded-xl p-0 overflow-hidden"
					style={{ maxWidth: '32rem' }}
					onInteractOutside={(e) => e.preventDefault()}
				>
					<DialogHeader className="px-6 pt-6 pb-4 border-b border-border">
						<DialogTitle>{edit ? 'Edit Custom Group' : 'New Custom Group'}</DialogTitle>
					</DialogHeader>

					<div className="px-6 py-6" data-testid="contact-list-form">
						{isFetching
							? <Spinner />
							: (
								<div className="space-y-4">
									<div className="space-y-1">
										<label className="text-sm font-medium">Name</label>
										<Input
											onChange={(e) => setName(e.target.value)}
											defaultValue={name}
											data-testid="contact-list-form-name-input"
										/>
									</div>
									<div className="space-y-1">
										<label className="text-sm font-medium">Contacts</label>
										<div data-testid="contact-list-form-contacts-input">
											<ContactPicker
												contacts={users}
												onUpdate={(contacts) => setRecipients([...groups, ...contacts])}
												exclude={[session]}
												allowGroups={false}
												allowEmails
												allowUsers
												placeholder="Search for users..."
											/>
										</div>
									</div>
									<div className="space-y-1">
										<label className="text-sm font-medium">Groups</label>
										<div data-testid="contact-list-form-contacts-input-groups">
											<ContactPicker
												contacts={groups}
												onUpdate={(contacts) => setRecipients([...users, ...contacts])}
												exclude={[session]}
												allowGroups
												allowEmails={false}
												allowUsers={false}
												placeholder="Search for groups..."
											/>
										</div>
									</div>
								</div>
							)}
					</div>

					<DialogFooter className="px-6 py-4 border-t border-border">
						<Button variant="outline" onClick={clearForm} disabled={isSaving}>
							Cancel
						</Button>
						<Button
							data-testid="contact-list-form-save-btn"
							onClick={handleSave}
							disabled={isSaveDisabled()}
						>
							{isSaving ? 'Saving…' : 'Save'}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
};

ContactListPreferences.propTypes = {
	currentPreferences: PropTypes.object.isRequired,
	isSaving: PropTypes.bool.isRequired,
	updatePreferences: PropTypes.func.isRequired,
	session: PropTypes.object.isRequired
};

export default ContactListPreferences;
